import { useCallback, useEffect, useMemo, useState } from "react";
import { getFriends } from "../../../functions/user";
import "./style.css";

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    if (value._id) return normalizeId(value._id);
    if (value.id) return normalizeId(value.id);
  }
  return String(value);
};

const formatDisplayName = (person) => {
  if (!person) return "Friend";
  const full = [person.first_name, person.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return full || person.name || person.username || "Friend";
};

export default function AddMembersModal({
  isVisible,
  onClose,
  onConfirm,
  currentUser,
  existingMemberIds = [],
  groupName = "this group",
}) {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const existingSet = useMemo(() => {
    const set = new Set();
    (existingMemberIds || []).forEach((id) => {
      const normalized = normalizeId(id);
      if (normalized) set.add(normalized);
    });
    return set;
  }, [existingMemberIds]);

  const loadFriends = useCallback(async () => {
    if (!currentUser?.token) return;
    try {
      setLoading(true);
      const friendsList = await getFriends(currentUser.token);
      if (!Array.isArray(friendsList)) {
        setFriends([]);
        return;
      }
      const currentUserId = normalizeId(currentUser.id || currentUser._id);
      const available = friendsList.filter((friend) => {
        const id = normalizeId(friend?._id || friend?.id);
        if (!id || id === currentUserId) return false;
        return !existingSet.has(id);
      });
      setFriends(available);
    } catch (error) {
      console.error("Error loading friends for Add Members:", error);
      setFriends([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser, existingSet]);

  useEffect(() => {
    if (!isVisible) {
      setSearchQuery("");
      setSelectedIds([]);
      setIsSubmitting(false);
      return;
    }
    loadFriends();
  }, [isVisible, loadFriends]);

  const filteredFriends = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return friends;
    return friends.filter((friend) => {
      const name = formatDisplayName(friend).toLowerCase();
      const username = String(friend?.username || "").toLowerCase();
      return name.includes(query) || username.includes(query);
    });
  }, [friends, searchQuery]);

  if (!isVisible) return null;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose && onClose();
  };

  const toggleFriend = (friendId) => {
    if (isSubmitting) return;
    setSelectedIds((prev) =>
      prev.includes(friendId)
        ? prev.filter((id) => id !== friendId)
        : [...prev, friendId]
    );
  };

  const handleConfirm = async () => {
    if (isSubmitting || selectedIds.length === 0 || !onConfirm) return;
    const selectedFriends = friends.filter((friend) =>
      selectedIds.includes(normalizeId(friend?._id || friend?.id))
    );
    try {
      setIsSubmitting(true);
      await onConfirm(selectedFriends);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add_members_modal_overlay" onClick={handleClose}>
      <div
        className="add_members_modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-members-title"
      >
        <div className="add_members_header">
          <div className="add_members_header_text">
            <h3 id="add-members-title">Add Members</h3>
            <p className="add_members_subtitle">
              Invite friends to {groupName}
            </p>
          </div>
          <button
            type="button"
            className="close_btn"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close add members"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <div className="add_members_search">
          <input
            type="search"
            className="add_members_search_input"
            placeholder="Search friends..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={isSubmitting}
            aria-label="Search friends"
          />
        </div>

        <div className="add_members_list" role="listbox" aria-multiselectable="true">
          {loading ? (
            <div className="add_members_empty">Loading friends…</div>
          ) : filteredFriends.length === 0 ? (
            <div className="add_members_empty">
              {friends.length === 0
                ? "No friends left to add"
                : "No friends match your search"}
            </div>
          ) : (
            filteredFriends.map((friend) => {
              const friendId = normalizeId(friend?._id || friend?.id);
              const displayName = formatDisplayName(friend);
              const isSelected = selectedIds.includes(friendId);
              return (
                <button
                  key={friendId}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`add_members_item ${isSelected ? "is_selected" : ""}`}
                  onClick={() => toggleFriend(friendId)}
                  disabled={isSubmitting}
                >
                  <img
                    src={friend?.picture || "/images/default_profile.png"}
                    alt=""
                    className="add_members_avatar"
                  />
                  <div className="add_members_info">
                    <span className="add_members_name">{displayName}</span>
                    {friend?.username && (
                      <span className="add_members_username">
                        @{friend.username}
                      </span>
                    )}
                  </div>
                  <span className="add_members_check" aria-hidden="true">
                    {isSelected ? "✓" : ""}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="add_members_actions">
          <button
            type="button"
            className="cancel_btn"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="add_members_confirm_btn"
            onClick={handleConfirm}
            disabled={isSubmitting || selectedIds.length === 0}
          >
            {isSubmitting
              ? "Adding…"
              : selectedIds.length > 0
                ? `Add (${selectedIds.length})`
                : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
